import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Row, SeasonCatalog } from 'types';
import { User } from 'types/api/users.type';

import { useAppSelector } from 'redux/hooks';

import { usersDataService } from 'services/users.service';

import { useUsersContext } from 'contexts/UsersContext';

import { mapSeasons } from 'helpers/mapSeasons';

import { EditForm } from 'sections/EditForm';
import { SectionType } from 'sections/TabWrap';

import { Card } from 'components/Card';
import { Table } from 'components/Table';

import { useColumns } from './columns.hooks';
import { useDataForm } from './useDataForm.hooks';

const useCategories = () => {
  const { id } = useParams();

  const { user, userSet, onSubmit, form } = useUsersContext();

  const [currentSeason, currentSeasonSet] = useState<SeasonCatalog>();

  const seasons = useAppSelector((state) => state.constants.seasons);
  const seasonsMapped = mapSeasons(seasons);
  const userSeasonsMapped = mapSeasons(user?.season!);

  // filtering to show only seasons that have remaining lines
  const seasonsFiltered = seasonsMapped
    .map((item) => {
      const season = userSeasonsMapped.filter((el) => el.season === item.season)[0];
      const remainingLines = item.catalog.filter((cat) => !season?.catalog.includes(cat));

      return { ...item, catalog: remainingLines };
    })
    .filter((obj) => obj.catalog.length > 0);

  const formData = useDataForm(
    seasonsFiltered,
    seasonsFiltered.find((el) => el?.season === currentSeason?.season)?.catalog
  );

  const season = form?.watch('season');

  const deleteSeason = (row: Row, currentUser?: User) => {
    const seasonValue = row.original.season;
    const catalogValue = row.original.catalog;

    if (currentUser) {
      const seasonsDeleted = currentUser
        ? currentUser.season.filter(
            (item) => item.season !== seasonValue || item.descEng !== catalogValue
          )
        : [];

      const userData = { ...currentUser, season: seasonsDeleted };

      if (id) {
        usersDataService.putUser(id, userData).then((res) => {
          if (userSet) {
            userSet(res.data);
          }
        });
      }
    }
  };

  const columns = useColumns(deleteSeason, user);

  const tableDataMapped = user?.season.map((item) => ({
    id: item?.season,
    season: item?.season,
    catalog: item?.descEng,
  }));

  const sections: SectionType[] = [
    {
      id: 1,
      content: (
        <Card>
          <EditForm
            hasButtons
            control={form?.control}
            register={form?.register}
            data={formData}
            onSubmit={onSubmit}
            isDirty={form?.formState?.isDirty}
            hideUndo
            initialValues={user}
            submitBtnLabel="+ add new category"
          />
        </Card>
      ),
    },
    {
      id: 2,
      content: (
        <Table
          hasSort
          initialSorting={[
            {
              id: 'season',
              desc: false,
            },
          ]}
          columns={columns}
          data={tableDataMapped || []}
        />
      ),
    },
  ];

  useEffect(() => {
    if (season) {
      currentSeasonSet(seasonsMapped.find((seasonItem) => seasonItem.season === season));
    }
  }, [season]);

  useEffect(() => {
    if (season && currentSeason && season !== currentSeason.season) {
      form?.setValue('catalog', []);
    }
  }, [season, currentSeason]);

  return {
    sections,
  };
};

export { useCategories };
